import type { DbExecutor } from "../executor";
import type { CostEvidence, EstimateRequest, EvidenceObservation, EvidenceProvider } from "../../estimating/evidence";
import { offerProvenanceLabel } from "../../sourcing/providers";

/**
 * CURRENT PRODUCT / OFFER EVIDENCE (spec §26D, building on §26A).
 *
 * What the thing actually costs to buy right now, from the append-only
 * price observations. Rules carried over unchanged:
 *  - an offer NEVER repriced a project selection by itself, and it does not
 *    here either: this is a RECOMMENDATION the user accepts or ignores;
 *  - a manual observation is labeled "Manual", never presented as live;
 *  - the market matters — an offer observed for another ZIP is included
 *    only when nothing local exists, and says which market it came from;
 *  - coverage converts a package price to a construction unit; if the
 *    coverage unit is not the unit being estimated, the offer is not
 *    offered as evidence at all (no silent unit conversion).
 */
export class ProductOfferProvider implements EvidenceProvider {
  readonly id = "product-offer";
  readonly label = "Current offer";
  readonly kind = "product-offer" as const;

  constructor(private readonly db: DbExecutor) {}

  async availability() {
    const rows = await this.db.select<{ n: number }>("SELECT COUNT(*) AS n FROM price_observations");
    const n = Number(rows[0]?.n ?? 0);
    return {
      available: true,
      note: n === 0 ? "No product prices recorded yet — add or source a product to build offer evidence." : null,
    };
  }

  async gather(request: EstimateRequest): Promise<CostEvidence[]> {
    const { subject } = request;
    if (subject.productId === null && subject.materialDefinitionId === null) return [];

    const rows = subject.productId
      ? await this.observationsForProduct(subject.productId)
      : await this.observationsForDefinition(subject.materialDefinitionId!);
    if (rows.length === 0) return [];

    // Newest observation per product, in this market when possible.
    const local = rows.filter((r) => (r.market_zip ?? "") === request.market.zip);
    const chosen = local.length > 0 ? local : rows;
    const byProduct = new Map<string, OfferRow>();
    for (const row of chosen) {
      const existing = byProduct.get(row.product_id);
      if (!existing || row.observed_at > existing.observed_at) byProduct.set(row.product_id, row);
    }

    const out: CostEvidence[] = [];
    for (const row of byProduct.values()) {
      if (row.coverage_unit !== subject.unit) continue; // never convert silently
      const coverage = Number(row.coverage_per_unit_x100);
      if (!coverage) continue;
      const unitCostCentsX100 = Math.round((Number(row.price_cents) * 10000) / coverage);
      const live = row.provider_id !== null && row.provider_id !== "";
      const sourceLabel = offerProvenanceLabel(row.provider_id);
      const observation: EvidenceObservation = {
        label: `${row.brand ? `${row.brand} ` : ""}${row.name}`,
        unitCostCentsX100,
        quantityX100: coverage,
        date: row.observed_at,
        projectJobNumber: null,
        projectId: null,
        market: row.market_zip || null,
        ref: { kind: "price-observation", id: row.id },
      };
      const explanation = [
        `${sourceLabel} price of ${formatCentsPlain(Number(row.price_cents))} per ${row.purchase_unit}.`,
        `One ${row.purchase_unit} covers ${(coverage / 100).toLocaleString("en-US")} ${row.coverage_unit}.`,
      ];
      if ((row.market_zip ?? "") !== request.market.zip) {
        explanation.push(
          row.market_zip
            ? `Observed for ZIP ${row.market_zip}, not ${request.market.zip} — prices vary by store.`
            : "No market recorded for this price.",
        );
      }
      if (!live) explanation.push("Entered by hand, so it is only as current as when you typed it.");
      out.push({
        id: `offer:${row.id}`,
        providerId: this.id,
        providerLabel: subject.productId === row.product_id ? "Selected product — current offer" : `Offer: ${row.name}`,
        kind: this.kind,
        unitCostCentsX100,
        unit: subject.unit,
        lowCentsX100: null,
        highCentsX100: null,
        sampleSize: 1,
        asOf: row.observed_at,
        market: row.market_zip || null,
        explanation,
        observations: [observation],
        quality: subject.productId === row.product_id && live ? "exact" : "strong",
      });
    }
    // The product actually selected on the line leads.
    out.sort((a, b) => (a.quality === "exact" ? -1 : b.quality === "exact" ? 1 : a.unitCostCentsX100 - b.unitCostCentsX100));
    return out.slice(0, 4);
  }

  private observationsForProduct(productId: string): Promise<OfferRow[]> {
    return this.db.select<OfferRow>(`${OFFER_SELECT} WHERE p.id = ? ORDER BY o.observed_at DESC LIMIT 20`, [productId]);
  }

  private observationsForDefinition(materialDefinitionId: string): Promise<OfferRow[]> {
    return this.db.select<OfferRow>(
      `${OFFER_SELECT} WHERE p.material_definition_id = ? AND p.status = 'active' ORDER BY o.observed_at DESC LIMIT 40`,
      [materialDefinitionId],
    );
  }
}

const OFFER_SELECT = `
  SELECT o.id, o.product_id, o.price_cents, o.observed_at, o.market_zip, o.provider_id,
         p.name, p.brand, p.purchase_unit, p.coverage_per_unit_x100, p.coverage_unit
    FROM price_observations o
    JOIN products p ON p.id = o.product_id`;

interface OfferRow {
  id: string;
  product_id: string;
  price_cents: number;
  observed_at: string;
  market_zip: string | null;
  provider_id: string | null;
  name: string;
  brand: string;
  purchase_unit: string;
  coverage_per_unit_x100: number;
  coverage_unit: string;
}

function formatCentsPlain(cents: number): string {
  return `$${Math.floor(cents / 100).toLocaleString("en-US")}.${String(cents % 100).padStart(2, "0")}`;
}
